import * as zod from 'zod';

export type Currency = 'USD';

export type Unsubscribe = () => void;

export const DistanceUnit = zod.union([
  zod.literal('meter'),
  zod.literal('millimeter'),
  zod.literal('inch'),
]);

export type DistanceUnit = zod.infer<typeof DistanceUnit>;

export const ForceUnit = zod.union([
  zod.literal('newton'),
  zod.literal('pound-force'),
]);

export type ForceUnit = zod.infer<typeof ForceUnit>;

export interface DistanceUnitInfo {
  unit: DistanceUnit;
  name: string;
  namePlural: string;
  abbreviation: string;
  decimalPlaces: number;
  multiplier: number;
  fromMeters: <V>(
    value: V,
    roundingKind?: 'round' | 'floor' | 'ceil',
  ) => V | number;
  toMeters: <V>(value: V) => V | number;
  /**
   * Converst the value from Meters to the current unit and rounds the value with the current unit
   * decimal places and appends the abbreviation.
   * @param value The value in Meters.
   * @param roundingKind the rounding kind between round floor and ceil.
   * @param excludeAbbreviation Whether to exclude the abbreviation from the string.
   * @returns the value in the current unit with the abbreviation appended.
   */
  toString: <V>(
    value: V,
    roundingKind?: 'round' | 'floor' | 'ceil',
    excludeAbbreviation?: boolean,
  ) => string;
}

export interface ForceUnitInfo {
  unit: ForceUnit;
  name: string;
  namePlural: string;
  abbreviation: string;
  decimalPlaces: number;
  multiplier: number;
  fromNewtons: <V>(
    value: V,
    roundingKind?: 'round' | 'floor' | 'ceil',
  ) => V | number;
  toNewtons: <V>(value: V) => V | number;
  /**
   * Converts the value from Newtons to the current unit and rounds the value with the current unit
   * decimal places and appends the abbreviation.
   * @param value The value in Newtons.
   * @param roundingKind the rounding kind between round floor and ceil.
   * @param excludeAbbreviation Whether to exclude the abbreviation from the string.
   * @returns the value in the current unit with the abbreviation appended.
   */
  toString: <V>(
    value: V,
    roundingKind?: 'round' | 'floor' | 'ceil',
    excludeAbbreviation?: boolean,
  ) => string;
}
